import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Loader2, GitMerge, AlertCircle, Eye, X, RefreshCw, CheckCircle, Building2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ConfidenceBadge, MatchDetails } from '@/components/deduplication';
import { findCompanyDuplicates, MatchResult } from '@/lib/deduplication-utils';
import { Link } from 'react-router-dom';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Company {
  id: string;
  name: string;
  website_url: string | null;
  location: string | null;
  category: string | null;
}

interface DuplicateGroup {
  primary: Company;
  duplicates: Array<{ company: Company; match: MatchResult }>;
}

interface GroupSelection {
  primaryId: string;
  selectedIds: Set<string>;
}

interface CompanyDeduplicationPanelProps {
  onMergeComplete?: () => void;
}

export const CompanyDeduplicationPanel = ({ onMergeComplete }: CompanyDeduplicationPanelProps) => {
  const [duplicates, setDuplicates] = useState<DuplicateGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<number | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [groupSelections, setGroupSelections] = useState<Record<number, GroupSelection>>({});
  const [ignoredPairs, setIgnoredPairs] = useState<Set<string>>(new Set());
  const { toast } = useToast();

  useEffect(() => {
    fetchIgnoredPairs().then(() => fetchAndDetectDuplicates());
  }, []);

  const fetchIgnoredPairs = async () => {
    try {
      const { data } = await supabase
        .from('duplicate_suggestions')
        .select('primary_entity_id, duplicate_entity_id')
        .eq('entity_type', 'company')
        .eq('status', 'ignored');

      if (data) {
        const pairs = new Set<string>();
        data.forEach(row => {
          pairs.add(`${row.primary_entity_id}-${row.duplicate_entity_id}`);
          pairs.add(`${row.duplicate_entity_id}-${row.primary_entity_id}`);
        });
        setIgnoredPairs(pairs);
      }
    } catch (error) {
      console.error('Error fetching ignored pairs:', error);
    }
  };

  const fetchAndDetectDuplicates = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('id, name, website_url, location, category')
        .order('name');

      if (error) throw error;

      const allGroups = findCompanyDuplicates(data || []) as DuplicateGroup[];
      
      // Filter out ignored pairs
      const filteredGroups = allGroups.map(group => {
        const filteredDuplicates = group.duplicates.filter(d => {
          const pairKey = `${group.primary.id}-${d.company.id}`;
          return !ignoredPairs.has(pairKey);
        });
        return { ...group, duplicates: filteredDuplicates };
      }).filter(group => group.duplicates.length > 0);

      setDuplicates(filteredGroups);

      // Initialize selections: all selected by default, first one as primary
      const initialSelections: Record<number, GroupSelection> = {};
      filteredGroups.forEach((group, idx) => {
        const allIds = [group.primary.id, ...group.duplicates.map(d => d.company.id)];
        initialSelections[idx] = {
          primaryId: group.primary.id,
          selectedIds: new Set(allIds),
        };
      });
      setGroupSelections(initialSelections);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch companies',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleCompanySelection = (groupIndex: number, companyId: string) => {
    setGroupSelections(prev => {
      const current = prev[groupIndex];
      const newSelected = new Set(current.selectedIds);

      if (newSelected.has(companyId)) {
        if (companyId === current.primaryId) {
          toast({ title: 'Cannot deselect primary company', variant: 'destructive' });
          return prev;
        }
        newSelected.delete(companyId);
      } else {
        newSelected.add(companyId);
      }

      return {
        ...prev,
        [groupIndex]: { ...current, selectedIds: newSelected }
      };
    });
  };

  const setPrimaryCompany = (groupIndex: number, companyId: string) => {
    setGroupSelections(prev => {
      const current = prev[groupIndex];
      const newSelected = new Set(current.selectedIds);
      newSelected.add(companyId);

      return {
        ...prev,
        [groupIndex]: { primaryId: companyId, selectedIds: newSelected }
      };
    });
  };

  const handleMergeSelected = async (groupIndex: number) => {
    const selection = groupSelections[groupIndex];
    const group = duplicates[groupIndex];

    const selectedCount = selection.selectedIds.size;
    if (selectedCount < 2) {
      toast({ title: 'Select at least 2 companies to merge', variant: 'destructive' });
      return;
    }

    const duplicatesToMerge = [...selection.selectedIds].filter(id => id !== selection.primaryId);

    if (!confirm(`Merge ${duplicatesToMerge.length} company(ies) into the primary? This action cannot be undone.`)) {
      return;
    }

    setProcessing(groupIndex);
    try {
      for (const duplicateId of duplicatesToMerge) {
        const duplicate = getAllCompaniesInGroup(group).find(c => c.company.id === duplicateId)?.company;

        // Update candidate_experience records
        await supabase
          .from('candidate_experience')
          .update({ company_id: selection.primaryId })
          .eq('company_id', duplicateId);

        // Create alias for the duplicate name
        if (duplicate) {
          await supabase
            .from('company_aliases')
            .insert({ company_id: selection.primaryId, raw_name: duplicate.name });
        }

        // Log the merge
        await supabase
          .from('company_dedupe_events')
          .insert({
            primary_company_id: selection.primaryId,
            merged_company_id: duplicateId,
            notes: 'Merged via Data Quality dashboard',
          });

        // Delete the duplicate
        await supabase.from('companies').delete().eq('id', duplicateId);
      }

      toast({ title: `Merged ${duplicatesToMerge.length} company(ies) successfully` });
      fetchAndDetectDuplicates();
      onMergeComplete?.();
    } catch (error: any) {
      console.error('Error merging companies:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to merge companies',
        variant: 'destructive',
      });
    } finally {
      setProcessing(null);
    }
  };

  const dismissGroup = async (groupIndex: number) => {
    const group = duplicates[groupIndex];
    
    // Persist ignored pairs to database
    const ignoreRecords = group.duplicates.map(d => ({
      entity_type: 'company',
      primary_entity_id: group.primary.id,
      duplicate_entity_id: d.company.id,
      status: 'ignored',
      match_score: d.match.score,
      reason: 'Dismissed by user',
    }));

    try {
      await supabase.from('duplicate_suggestions').insert(ignoreRecords);
      
      // Update local ignored pairs
      const newIgnored = new Set(ignoredPairs);
      group.duplicates.forEach(d => {
        newIgnored.add(`${group.primary.id}-${d.company.id}`);
        newIgnored.add(`${d.company.id}-${group.primary.id}`);
      });
      setIgnoredPairs(newIgnored);
    } catch (error) {
      console.error('Error saving ignored suggestion:', error);
    }

    setDuplicates(prev => prev.filter((_, i) => i !== groupIndex));
    setGroupSelections(prev => {
      const updated = { ...prev };
      delete updated[groupIndex];
      return updated;
    });
    toast({ title: 'Duplicate group ignored and will not appear again' });
  };

  const getAllCompaniesInGroup = (group: DuplicateGroup) => {
    return [
      { company: group.primary, match: null as MatchResult | null },
      ...group.duplicates.map(d => ({ company: d.company, match: d.match }))
    ];
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Analyzing company records...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Building2 className="h-5 w-5 text-emerald-500" />
            Company Deduplication
          </h2>
          <p className="text-sm text-muted-foreground">
            Select companies to merge and choose which one to keep as primary
          </p>
        </div>
        <Button onClick={fetchAndDetectDuplicates} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Rescan
        </Button>
      </div>

      {duplicates.length === 0 ? (
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <CardContent className="flex items-center gap-4 p-6">
            <div className="rounded-full bg-emerald-500/20 p-3">
              <CheckCircle className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold">No Duplicates Found</h3>
              <p className="text-sm text-muted-foreground">Your company data looks clean!</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Found {duplicates.length} potential duplicate group{duplicates.length !== 1 ? 's' : ''}.
              Use checkboxes to select which companies to merge and radio buttons to choose the primary.
            </AlertDescription>
          </Alert>

          <div className="space-y-4">
            {duplicates.map((group, groupIndex) => {
              const selection = groupSelections[groupIndex];
              if (!selection) return null;

              const allCompanies = getAllCompaniesInGroup(group);
              const selectedCount = selection.selectedIds.size;
              const isProcessing = processing === groupIndex;

              return (
                <Card key={group.primary.id} className="overflow-hidden">
                  <CardHeader className="bg-muted/50 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="rounded-full bg-emerald-500/20 p-2">
                          <Building2 className="h-4 w-4 text-emerald-500" />
                        </div>
                        <div>
                          <CardTitle className="text-base">
                            Duplicate Group {groupIndex + 1}
                          </CardTitle>
                          <CardDescription>
                            {selectedCount} of {allCompanies.length} selected for merge
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <ConfidenceBadge
                          confidence={group.duplicates[0]?.match.confidence || 'none'}
                          score={group.duplicates[0]?.match.score || 0}
                        />
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => dismissGroup(groupIndex)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Dismiss this group</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-4 space-y-3">
                    <RadioGroup
                      value={selection.primaryId}
                      onValueChange={(value) => setPrimaryCompany(groupIndex, value)}
                      className="space-y-2"
                    >
                      {allCompanies.map(({ company, match }) => {
                        const isSelected = selection.selectedIds.has(company.id);
                        const isPrimary = selection.primaryId === company.id;

                        return (
                          <div
                            key={company.id}
                            className={`border rounded-lg p-4 transition-all ${
                              isPrimary
                                ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                                : isSelected
                                  ? 'border-border bg-muted/30'
                                  : 'border-border/50 bg-muted/10 opacity-60'
                            }`}
                          >
                            <div className="flex items-start gap-4">
                              {/* Checkbox for inclusion */}
                              <div className="flex items-center gap-3 pt-1">
                                <Checkbox
                                  id={`check-${company.id}`}
                                  checked={isSelected}
                                  onCheckedChange={() => toggleCompanySelection(groupIndex, company.id)}
                                  disabled={isPrimary}
                                />
                                <RadioGroupItem
                                  value={company.id}
                                  id={`radio-${company.id}`}
                                  disabled={!isSelected}
                                />
                              </div>

                              {/* Company Info */}
                              <div className="flex-1 space-y-1">
                                <Label
                                  htmlFor={`radio-${company.id}`}
                                  className="flex items-center gap-2 cursor-pointer font-semibold"
                                >
                                  {company.name}
                                  {isPrimary && (
                                    <Badge variant="default" className="text-xs">Primary</Badge>
                                  )}
                                </Label>
                                <div className="text-sm text-muted-foreground space-y-0.5">
                                  {company.location && <p>{company.location}</p>}
                                  {company.website_url && <p>{company.website_url}</p>}
                                  {company.category && <p className="capitalize">{company.category}</p>}
                                </div>
                                {match && <MatchDetails details={match.details} />}
                              </div>

                              {/* Actions */}
                              <div className="flex items-center gap-2">
                                {match && (
                                  <ConfidenceBadge confidence={match.confidence} score={match.score} />
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setSelectedCompany(company)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </RadioGroup>

                    {/* Merge Button */}
                    <Button
                      className="w-full gap-2"
                      onClick={() => handleMergeSelected(groupIndex)}
                      disabled={isProcessing || selectedCount < 2}
                    >
                      {isProcessing ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <GitMerge className="h-4 w-4" />
                      )}
                      Merge {selectedCount - 1} Selected into Primary
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Company Preview Dialog */}
      <Dialog open={!!selectedCompany} onOpenChange={() => setSelectedCompany(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Company Details</DialogTitle>
            <DialogDescription>
              Preview company information
            </DialogDescription>
          </DialogHeader>
          {selectedCompany && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg">{selectedCompany.name}</h3>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Website</p>
                  <p>{selectedCompany.website_url || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Location</p>
                  <p>{selectedCompany.location || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Category</p>
                  <p className="capitalize">{selectedCompany.category || 'Not provided'}</p>
                </div>
              </div>
              <div className="flex justify-end">
                <Link to={`/recruiter/companies?id=${selectedCompany.id}`}>
                  <Button variant="outline">View Full Profile</Button>
                </Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
